import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import baseURL from '../../../baseURL';

const formatDate = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}/${x.getFullYear()}`;
};

const AuctionReserveTab = () => {
  const [catalogueNo, setCatalogueNo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [narration, setNarration] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  const fetchReserve = () => {
    if (!catalogueNo.trim()) {
      setError('Enter Catalogue Number');
      return;
    }
    setError('');
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.get(`${baseURL}/auction/reserve`, { params: { catalogueNo: catalogueNo.trim() }, headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setData(res.data.data))
      .catch((err) => {
        setError(err.response?.data?.message || err.message);
        setData(null);
      })
      .finally(() => setLoading(false));
  };

  const submitPayment = () => {
    const amt = Number(paymentAmount);
    if (!amt || amt <= 0 || !paymentDate) {
      setError('Amount (positive) and date are required');
      return;
    }
    setError('');
    setPaymentLoading(true);
    const token = localStorage.getItem('token');
    axios.post(
      `${baseURL}/auction/reserve/payment`,
      { amount: amt, date: paymentDate, narration: narration || undefined },
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then(() => {
        setPaymentOpen(false);
        setPaymentAmount('');
        setNarration('');
        fetchReserve();
      })
      .catch((err) => setError(err.response?.data?.message || err.message))
      .finally(() => setPaymentLoading(false));
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Auction Reserve Account (Pawn Reserve Acc)</Typography>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField label="Catalogue Number" value={catalogueNo} onChange={(e) => setCatalogueNo(e.target.value)} size="small" placeholder="e.g. 1/2026" />
        <Button variant="contained" onClick={fetchReserve} disabled={loading}>{loading ? <CircularProgress size={24} /> : 'Show'}</Button>
        <Button variant="outlined" onClick={() => setPaymentOpen(true)}>Pay from Reserve (to client)</Button>
      </Box>
      {data && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1">Catalogue: {data.catalogueNo}</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>PTNO</TableCell>
                  <TableCell>DATE</TableCell>
                  <TableCell>AMOUNT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.entries || []).map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.PTNO}</TableCell>
                    <TableCell>{formatDate(row.DATE)}</TableCell>
                    <TableCell>{row.AMOUNT?.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Total: {data.total?.toFixed(2)}</Typography>
          <Typography variant="caption" display="block" color="text.secondary">Balance appears in Balance Sheet under Liability.</Typography>
        </Paper>
      )}

      <Dialog open={paymentOpen} onClose={() => !paymentLoading && setPaymentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Payment from Pawn Reserve Acc</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Amount" type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} size="small" />
            <TextField label="Date" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
            <TextField label="Narration" value={narration} onChange={(e) => setNarration(e.target.value)} size="small" multiline rows={2} placeholder="e.g. Payment to client – excess from auction" />
          </Box>
          <Typography variant="caption" color="text.secondary">Debit: Pawn Reserve Acc, Credit: Cash in Hand</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitPayment} disabled={paymentLoading}>
            {paymentLoading ? <CircularProgress size={24} /> : 'Post Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuctionReserveTab;
